import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, X, Sparkles } from "lucide-react";
import { AuthContext } from "@/App";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_ACTIONS = {
  create_account: null,
  generate_api_key: "/dashboard?tab=api-keys",
  first_api_call: "/dashboard?tab=docs",
  review_optimization: "/dashboard?tab=savings",
};

export default function OnboardingChecklist() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (user && !user.is_demo) fetchStatus();
  }, [user]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`);
      setStatus(res.data);
      if (res.data.dismissed) setDismissed(true);
      if (res.data.all_complete && !res.data.dismissed) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
    } catch {}
  };

  const handleDismiss = async () => {
    try {
      await axios.post(`${API}/onboarding/dismiss`);
      setDismissed(true);
    } catch {}
  };

  if (!status || dismissed || !status.steps?.length || status.dismissed) return null;

  const completed = status.steps.filter(s => s.completed).length;
  const total = status.steps.length;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
        <Card className="bg-[var(--qn-card)] border-[var(--qn-primary)]/20 relative overflow-hidden" data-testid="onboarding-checklist">
          {/* Confetti effect */}
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
              <Sparkles className="w-16 h-16 text-[var(--qn-primary)] animate-bounce" />
            </div>
          )}

          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--qn-primary)]/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[var(--qn-primary)]" />
                </div>
                <div>
                  <div className="text-sm font-semibold">Get Started with QNNEAL</div>
                  <div className="text-xs text-[var(--qn-foreground-muted)]">{completed}/{total} complete</div>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]" data-testid="dismiss-onboarding">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-[var(--qn-background)] rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${status.percentage}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-[var(--qn-primary)] rounded-full"
              />
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {status.steps.map((step) => (
                <div key={step.id} className={`flex items-center justify-between py-2 px-3 rounded-lg transition-all ${step.completed ? "bg-emerald-500/5" : "bg-[var(--qn-background)] hover:bg-[var(--qn-background)]/80"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${step.completed ? "bg-emerald-500 border-emerald-500" : "border-[var(--qn-border)]"}`}>
                      {step.completed && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm ${step.completed ? "text-[var(--qn-foreground-muted)] line-through" : "font-medium"}`}>
                      {step.label}
                    </span>
                  </div>
                  {!step.completed && STEP_ACTIONS[step.id] && (
                    <Button size="sm" variant="ghost" className="text-[var(--qn-primary)] h-7 text-xs" onClick={() => navigate(STEP_ACTIONS[step.id])} data-testid={`onboarding-action-${step.id}`}>
                      Do it <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                  {step.completed && (
                    <span className="text-xs text-emerald-400">Done</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}
